/**
 * ServicelineCheckCard
 *
 * QA tool for verifying captured-claims service line totals against
 * individual code counts. Works for ALL service lines (custom and standard).
 *
 * For a selected provider + service line it:
 *   1. Fetches every code in the service line
 *   2. Sums the provider's claim_count across all location types
 *      (NULL = suppressed <11 claims, counted as 5 per CMS rule)
 *   3. Compares that computed sum against the stored total_claims
 *   4. Shows code count, computed sum, and Market Data total
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import axiosInstance from 'utils/axiosUtil';
import { StoreUtil } from 'doctivity-shared/utils';
import { FindProviderDialog } from 'components';
import { FindOrganizationDialog } from 'organizations';

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  card: {
    marginBottom: theme.spacing(3),
  },
  cardInnerHeader: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  cardTitle: {
    flex: 1,
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
  },
  controlItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  controlLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  controlValue: {
    fontSize: 13,
  },
  entityToggle: {
    marginBottom: theme.spacing(1),
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      padding: '4px 14px',
      fontSize: 13,
    },
  },
  servicelineSelect: {
    minWidth: 280,
  },
  slParent: {
    borderTop: '1px solid #cccccc',
  },
  slChild: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  slCustom: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
    padding: theme.spacing(2),
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  summaryMatch: {
    backgroundColor: '#e8f5e9',
  },
  summaryMismatch: {
    backgroundColor: '#ffebee',
  },
  summaryNeutral: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 2,
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  summaryValueMismatch: {
    color: '#c62828',
  },
  summaryValueMatch: {
    color: '#2e7d32',
  },
  chipMatch: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    fontSize: 12,
  },
  chipMismatch: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 600,
    fontSize: 12,
  },
  delta: {
    fontSize: 12,
    color: '#c62828',
    fontWeight: 600,
    marginTop: 2,
  },
  marketLink: {
    marginLeft: 'auto',
    fontSize: 13,
    whiteSpace: 'nowrap',
    alignSelf: 'center',
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  errorMsg: {
    fontSize: 13,
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
});

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n) {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString();
}

// ── Service line dropdown builder ──────────────────────────────────────────────

function slLabel(sl) {
  return sl.is_diagnosis != null
    ? `${sl.name} (${sl.is_diagnosis ? 'Dx' : 'Px'})`
    : sl.name;
}

function buildServicelineMenuItems(servicelines, classes) {
  const custom  = servicelines.filter((sl) => sl.client_id != null);
  const standard = servicelines.filter((sl) => sl.client_id == null);

  // Build parent → children map for standard service lines
  const parentMap = new Map();  // id → sl
  const childMap  = new Map();  // parent_id → sl[]
  standard.forEach((sl) => {
    if (sl.parent_id == null) {
      parentMap.set(sl.id, sl);
    } else {
      if (!childMap.has(sl.parent_id)) childMap.set(sl.parent_id, []);
      childMap.get(sl.parent_id).push(sl);
    }
  });
  // Orphan children whose parent isn't in the list get shown as top-level
  const orphans = standard.filter(
    (sl) => sl.parent_id != null && !parentMap.has(sl.parent_id)
  );

  const items = [];

  // ── Custom section ──────────────────────────────────────────────────────────
  if (custom.length > 0) {
    items.push(
      <ListSubheader key='hdr-custom' style={{ lineHeight: '28px', fontSize: 11 }}>
        Custom Service Lines
      </ListSubheader>
    );
    custom.forEach((sl) => {
      items.push(
        <MenuItem key={sl.id} value={sl.id} className={classes.slCustom}>
          {slLabel(sl)}
        </MenuItem>
      );
    });
    items.push(<Divider key='divider-custom' />);
  }

  // ── Standard section — parents with indented children ──────────────────────
  parentMap.forEach((parent) => {
    items.push(
      <MenuItem key={parent.id} value={parent.id} className={classes.slParent}>
        {slLabel(parent)}
      </MenuItem>
    );
    (childMap.get(parent.id) || []).forEach((child) => {
      items.push(
        <MenuItem key={child.id} value={child.id} className={classes.slChild}>
          {slLabel(child)}
        </MenuItem>
      );
    });
  });

  // Orphan children (parent not available for this provider)
  orphans.forEach((sl) => {
    items.push(
      <MenuItem key={sl.id} value={sl.id} className={classes.slParent}>
        {slLabel(sl)}
      </MenuItem>
    );
  });

  return items;
}

// ── Codes dialog ───────────────────────────────────────────────────────────────

function CodesDialog({ open, onClose, serviceline, codeStrings }) {
  const [copied, setCopied] = React.useState(false);
  const joined = codeStrings.join(', ');

  const handleCopy = () => {
    navigator.clipboard.writeText(joined).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        Codes in {serviceline?.name} ({codeStrings.length} codes)
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant='body2'
          style={{
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            lineHeight: 1.8,
            userSelect: 'all',
          }}
        >
          {joined || '—'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCopy} color='primary' size='small'>
          {copied ? '✓ Copied' : 'Copy All'}
        </Button>
        <Button onClick={onClose} size='small'>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

class ServicelineCheckCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      entityType: 'provider',  // 'provider' | 'org'
      provider: null,
      providerDialogOpen: false,
      org: null,
      orgDialogOpen: false,
      servicelines: [],
      servicelineId: '',
      servicelineLoading: false,
      checking: false,
      result: null,
      error: null,
      codesDialogOpen: false,
    };
  }

  componentDidUpdate(prev) {
    const { app } = this.props;
    if (app.selectedClient !== prev.app.selectedClient) {
      this.setState({ provider: null, org: null, servicelines: [], servicelineId: '', result: null });
    }
  }

  onEntityTypeChange = (_, newType) => {
    if (!newType) return;
    this.setState({
      entityType: newType,
      provider: null, org: null,
      servicelines: [], servicelineId: '', result: null, error: null,
    });
  };

  loadServicelines = async (entityType, entityId) => {
    this.setState({ servicelineLoading: true, servicelines: [], servicelineId: '', result: null });
    try {
      const isProvider = entityType === 'provider';
      const res = await axiosInstance.get(
        isProvider ? '/v2/qa/provider-servicelines' : '/v2/qa/org-servicelines',
        { params: isProvider ? { providerId: entityId } : { orgId: entityId } }
      );
      this.setState({ servicelines: res.data || [], servicelineLoading: false });
    } catch (err) {
      this.setState({ servicelineLoading: false, error: 'Failed to load service lines' });
    }
  };

  // ── Provider dialog ──────────────────────────────────────────────────────────

  openProviderDialog  = () => this.setState({ providerDialogOpen: true });
  closeProviderDialog = () => this.setState({ providerDialogOpen: false });
  onProviderSelected  = (provider) => {
    this.setState({ provider, providerDialogOpen: false });
    this.loadServicelines('provider', provider.id);
  };

  // ── Org dialog ───────────────────────────────────────────────────────────────

  openOrgDialog  = () => this.setState({ orgDialogOpen: true });
  closeOrgDialog = () => this.setState({ orgDialogOpen: false });
  onOrgSelected  = (org) => {
    if (!org) { this.setState({ orgDialogOpen: false }); return; }
    this.setState({ org, orgDialogOpen: false });
    this.loadServicelines('org', org.id);
  };

  // ── Run check ────────────────────────────────────────────────────────────────

  runCheck = async () => {
    const { entityType, provider, org, servicelineId } = this.state;
    const entity = entityType === 'provider' ? provider : org;
    if (!entity || !servicelineId) return;

    this.setState({ checking: true, error: null, result: null });
    try {
      const isProvider = entityType === 'provider';
      const res = await axiosInstance.get(
        isProvider ? '/v2/qa/serviceline-check' : '/v2/qa/org-serviceline-check',
        { params: isProvider
            ? { providerId: entity.id, servicelineId }
            : { orgId: entity.id, servicelineId }
        }
      );
      this.setState({ checking: false, result: res.data });
    } catch (err) {
      this.setState({
        checking: false,
        error: err.response?.data?.error || err.message || 'Check failed',
      });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  renderSummary() {
    const { classes } = this.props;
    const { result } = this.state;
    if (!result) return null;

    const { computedSum, storedTotal, match, serviceline, codes } = result;
    const { entityType } = this.state;
    const codeCount = Array.isArray(codes) ? codes.length : 0;
    const hasCodes = codeCount > 0;
    // computedSum === null means codes exist in the mapping but the entity has
    // no individual code-level data in the Dx/Px code count tables.
    const hasCountData = computedSum !== null;
    const claimsType = entityType === 'provider' ? 'providers' : 'organizations';
    const marketDataUrl = `/claims?claimsType=${claimsType}&patientType=CAPTURED_CLAIMS&serviceline=${serviceline.id}`;

    const summaryClass = (!hasCodes || !hasCountData)
      ? classes.summaryNeutral
      : match === true
      ? classes.summaryMatch
      : match === false
      ? classes.summaryMismatch
      : classes.summaryNeutral;

    const valueClass = (!hasCodes || !hasCountData) ? '' : match === false
      ? classes.summaryValueMismatch
      : match === true
      ? classes.summaryValueMatch
      : '';

    return (
      <div className={`${classes.summary} ${summaryClass}`}>
        {hasCodes && hasCountData && match !== null && (
          <Chip
            label={match ? '✓ Match' : '✗ Mismatch'}
            className={match ? classes.chipMatch : classes.chipMismatch}
          />
        )}

        <div className={classes.summaryItem}>
          <Typography className={classes.summaryLabel}>Codes in Service Line</Typography>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <Typography className={classes.summaryValue}>
              {hasCodes ? fmt(codeCount) : '—'}
            </Typography>
            {hasCodes && (
              <Typography
                component='span'
                style={{ fontSize: 11, color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => this.setState({ codesDialogOpen: true })}
              >
                show
              </Typography>
            )}
          </div>
          {!hasCodes && (
            <Typography style={{ fontSize: 11, color: '#999' }}>Not mapped in system</Typography>
          )}
        </div>

        <div className={classes.summaryItem}>
          <Typography className={classes.summaryLabel}>Computed Sum</Typography>
          <Typography className={`${classes.summaryValue} ${valueClass}`}>
            {hasCodes && hasCountData ? fmt(computedSum) : '—'}
          </Typography>
          {hasCodes && !hasCountData && (
            <Typography style={{ fontSize: 11, color: '#999' }}>No code data for entity</Typography>
          )}
        </div>

        <div className={classes.summaryItem}>
          <Typography className={classes.summaryLabel}>Market Data Shows</Typography>
          <Typography className={`${classes.summaryValue} ${valueClass}`}>
            {storedTotal !== null ? fmt(storedTotal) : '—'}
          </Typography>
          {hasCodes && hasCountData && match === false && (
            <Typography className={classes.delta}>
              Δ {fmt(Math.abs(computedSum - storedTotal))}
            </Typography>
          )}
          {hasCodes && hasCountData && match === null && (
            <Typography style={{ fontSize: 11, color: '#999' }}>No stored total found</Typography>
          )}
        </div>

        <Link to={marketDataUrl} target='_blank' className={classes.marketLink}>
          View in Market Data ↗
        </Link>
      </div>
    );
  }

  render() {
    const { classes, app, user } = this.props;
    const {
      entityType,
      provider,
      providerDialogOpen,
      org,
      orgDialogOpen,
      servicelines,
      servicelineId,
      servicelineLoading,
      checking,
      result,
      error,
    } = this.state;

    const isProvider = entityType === 'provider';
    const entity = isProvider ? provider : org;
    const canRun = !!entity && !!servicelineId && !checking;

    return (
      <Card className={classes.card} elevation={2}>
        <CardContent>
          {/* Header */}
          <div className={classes.cardInnerHeader}>
            <Typography component='h2' variant='h6' color='primary' gutterBottom className={classes.cardTitle}>
              Captured Claims Service Line Check
            </Typography>
            <Button
              color='primary'
              size='small'
              onClick={this.runCheck}
              disabled={!canRun}
              startIcon={
                checking
                  ? <CircularProgress size={16} color='inherit' />
                  : <Icon fontSize='small'>check_circle</Icon>
              }
            >
              {checking ? 'Checking…' : 'Run Check'}
            </Button>
          </div>

          {/* Entity type toggle */}
          <ToggleButtonGroup
            value={entityType}
            exclusive
            onChange={this.onEntityTypeChange}
            size='small'
            className={classes.entityToggle}
          >
            <ToggleButton value='provider'>Provider</ToggleButton>
            <ToggleButton value='org'>Organization</ToggleButton>
          </ToggleButtonGroup>

          {/* Controls */}
          <div className={classes.controls}>
            {/* Entity selector */}
            <div className={classes.controlItem}>
              <Typography className={classes.controlLabel}>
                {isProvider ? 'Provider:' : 'Organization:'}
              </Typography>
              {entity ? (
                <Typography className={classes.controlValue}>
                  {isProvider
                    ? `${entity.first_name} ${entity.last_name}`
                    : entity.name}
                </Typography>
              ) : (
                <Typography className={classes.controlValue} style={{ color: '#999' }}>
                  None selected
                </Typography>
              )}
              <Button
                size='small'
                color='primary'
                onClick={isProvider ? this.openProviderDialog : this.openOrgDialog}
                disabled={checking}
                style={{ marginLeft: 4, minWidth: 0, padding: '0 6px', fontSize: 11 }}
              >
                {entity ? 'Change' : 'Select'}
              </Button>
              {isProvider && provider && (
                <Link
                  to={`/providers/${provider.id}/codes`}
                  target='_blank'
                  style={{ marginLeft: 6, fontSize: 11 }}
                >
                  View Captured Claims ↗
                </Link>
              )}
            </div>

            {/* Service line */}
            <div className={classes.controlItem}>
              <FormControl size='small' className={classes.servicelineSelect}>
                <InputLabel>Select Service Line</InputLabel>
                <Select
                  value={servicelineId}
                  label='Select Service Line'
                  onChange={(e) => this.setState({ servicelineId: e.target.value, result: null })}
                  disabled={!entity || checking || servicelineLoading || servicelines.length === 0}
                >
                  {servicelineLoading && (
                    <MenuItem disabled value=''>Loading…</MenuItem>
                  )}
                  {!servicelineLoading && !entity && (
                    <MenuItem disabled value=''>
                      {isProvider ? 'Select a provider first' : 'Select an organization first'}
                    </MenuItem>
                  )}
                  {!servicelineLoading && entity && servicelines.length === 0 && (
                    <MenuItem disabled value=''>No service lines with claims found</MenuItem>
                  )}
                  {!servicelineLoading && entity && servicelines.length > 0 &&
                    buildServicelineMenuItems(servicelines, classes)
                  }
                </Select>
              </FormControl>
              {servicelineLoading && <CircularProgress size={18} />}
            </div>
          </div>

          {/* Provider dialog */}
          <FindProviderDialog
            open={providerDialogOpen}
            onSelect={this.onProviderSelected}
            clientId={app.selectedClient}
            userId={user?.id}
            onClose={this.closeProviderDialog}
          />

          {/* Org dialog */}
          <FindOrganizationDialog
            open={orgDialogOpen}
            onSelect={this.onOrgSelected}
            clientId={app.selectedClient}
          />

          {/* Error */}
          {error && <Typography className={classes.errorMsg}>{error}</Typography>}

          {/* Summary */}
          {result ? (
            this.renderSummary()
          ) : (
            !checking && (
              <Typography className={classes.noData}>
                Select a {isProvider ? 'provider' : 'organization'} and service line, then press Run Check.
              </Typography>
            )
          )}

          {/* Codes dialog */}
          {result && (
            <CodesDialog
              open={this.state.codesDialogOpen}
              onClose={() => this.setState({ codesDialogOpen: false })}
              serviceline={result.serviceline}
              codeStrings={result.codeStrings || []}
            />
          )}
        </CardContent>
      </Card>
    );
  }
}

ServicelineCheckCard.propTypes = {
  classes: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  const { app, user, clients } = state;
  const clientList = StoreUtil.get(clients, StoreUtil.COMMON_LIST);
  const client = clientList?.data
    ? clientList.data.find((c) => c.id === app.selectedClient)
    : null;
  return { app, user: user || {}, client };
}

const styled = withStyles(styles)(ServicelineCheckCard);
const connected = connect(mapStateToProps)(styled);
export { connected as ServicelineCheckCard };
